import { Injectable, inject } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import {
  SseEventType,
  SseEventPayload,
  BulkActionCompleteEvent,
} from 'portal-shared';
import { SseService } from './sse.service';
import { AdminBadgeService } from './admin-badge.service';
import { GlobalNotificationService } from '@core/services/global-notification.service';
import { AuthService } from '@core/services/auth.service';

type AccessAction = 'sent' | 'approved' | 'dismissed' | 'revoked';

/**
 * Listens to the backend SSE stream for bulk-action completion events and
 * surfaces a toast — independent of which page is open, so notifications still
 * arrive after the admin navigates away from the page that started the action.
 */
@Injectable({ providedIn: 'root' })
export class BulkActionNotifierService {
  private readonly sseService = inject(SseService);
  private readonly notificationService = inject(GlobalNotificationService);
  private readonly adminBadgeService = inject(AdminBadgeService);
  private readonly authService = inject(AuthService);

  private subscription: Subscription | null = null;

  private readonly analystChanged$ = new Subject<void>();
  private readonly participantChanged$ = new Subject<void>();

  /** Emits when an analyst/influencer bulk action completes. */
  readonly analystChanged: Observable<void> =
    this.analystChanged$.asObservable();
  /** Emits when a participant bulk action completes. */
  readonly participantChanged: Observable<void> =
    this.participantChanged$.asObservable();

  /**
   * Begin listening for bulk-action completion events. Safe to call repeatedly;
   * only the first call opens the SSE connection.
   */
  start(): void {
    if (this.subscription) return;
    this.sseService.connectAdmin();
    this.subscription = this.sseService.adminEvents.subscribe((event) =>
      this.handleEvent(event),
    );
  }

  /** Stop listening and close the SSE connection (e.g. on leaving admin). */
  stop(): void {
    this.subscription?.unsubscribe();
    this.subscription = null;
    this.sseService.disconnectAdmin();
  }

  private handleEvent(event: SseEventPayload): void {
    switch (event.type) {
      case SseEventType.ANALYST_INVITATION_COMPLETE:
        this.showAccessNotification(event, 'sent', 'influencer');
        this.analystChanged$.next();
        break;
      case SseEventType.ANALYST_APPROVE_COMPLETE:
        this.showAccessNotification(event, 'approved', 'influencer');
        this.adminBadgeService.refreshPendingCount();
        this.analystChanged$.next();
        break;
      case SseEventType.ANALYST_DISMISS_COMPLETE:
        this.showAccessNotification(event, 'dismissed', 'influencer');
        this.adminBadgeService.refreshPendingCount();
        this.analystChanged$.next();
        break;
      case SseEventType.ANALYST_REVOKE_COMPLETE:
        this.showAccessNotification(event, 'revoked', 'influencer');
        this.analystChanged$.next();
        break;
      case SseEventType.PARTICIPANT_INVITATION_COMPLETE:
        this.showAccessNotification(event, 'sent', 'participant');
        this.participantChanged$.next();
        break;
      case SseEventType.PARTICIPANT_REVOKE_COMPLETE:
        this.showAccessNotification(event, 'revoked', 'participant');
        this.participantChanged$.next();
        break;
    }
  }

  private showAccessNotification(
    event: BulkActionCompleteEvent,
    action: AccessAction,
    label: string,
  ): void {
    // The stream is site-wide; only the admin who requested the action gets the toast.
    if (event.userId !== this.authService.userResource.value()?.userId) {
      return;
    }

    const skipped = event.skipped ?? 0;
    const isInvitation = action === 'sent';
    const actionLabels = {
      sent: {
        verb: 'send',
        skippedTitle: `No invitations sent, ${skipped} already connected`,
      },
      approved: {
        verb: 'approve',
        skippedTitle: `No approvals made, ${skipped} already approved`,
      },
      dismissed: {
        verb: 'dismiss',
        skippedTitle: `No dismissals made, ${skipped} already dismissed`,
      },
      revoked: {
        verb: 'revoke',
        skippedTitle: `No access revoked, ${skipped} already revoked`,
      },
    }[action];
    const noun = isInvitation ? `${label} invitation(s)` : `${label}(s)`;

    if (event.succeeded > 0) {
      const skippedSuffix = skipped > 0 ? `, ${skipped} already connected` : '';
      const failedSuffix = event.failed > 0 ? `, ${event.failed} failed` : '';
      const title = isInvitation
        ? `Sent ${event.succeeded} ${noun}${skippedSuffix}${failedSuffix}`
        : `${event.succeeded} ${noun} ${action}${failedSuffix}`;
      this.notificationService.show({ type: 'success', title });
    } else if (event.failed > 0) {
      this.notificationService.show({
        type: 'error',
        title: `Failed to ${actionLabels.verb} ${event.failed} ${noun}`,
      });
    } else if (skipped > 0) {
      this.notificationService.show({
        type: 'info',
        title: actionLabels.skippedTitle,
      });
    }
  }
}
